import type { ProBrowseCard as ProBrowseCardData } from "../../lib/marketplace-discovery-api";
import { BrowseWaitlistState } from "../marketplace/BrowseWaitlistState";
import { SectionLabel } from "../marketplace/OffersInbox";
import { ProBrowseCard } from "../marketplace/ProBrowseCard";
import {
  agentPreviewSectionLabel,
  topAgentsBySavings,
  type AgentPreviewSectionState,
} from "./AgentPreview.helpers";

export interface AgentPreviewProps {
  agents: readonly ProBrowseCardData[];
  /** `access === "area_not_launched"` — renders `BrowseWaitlistState` in
   *  place of the agent list instead of hiding the block entirely. */
  areaNotLaunched: boolean;
  areaLabel: string;
  /** Browse `launched_areas` — threaded into `BrowseWaitlistState` at
   *  `area_not_launched` so the embedded block can name where Sill is live. */
  launchedAreas?: readonly string[];
  sectionState: AgentPreviewSectionState;
  /** Buyer tier string (e.g. "t1") — only used for the unpublished copy. */
  tier?: string;
  profileHref?: (proUserId: string) => string;
  onAgentClick?: (position: number) => void;
}

const DEFAULT_PROFILE_HREF = (proUserId: string) => `/market/agents/${proUserId}`;

/**
 * Plan 435 M3 (mfh-agent-preview). First three `getMarketplaceBrowse`
 * agents by `estimated_savings` desc, rendered as compact `ProBrowseCard`
 * tiles (design §4 row 3 / §4.1). Zero agents hides the block entirely;
 * `area_not_launched` renders `BrowseWaitlistState` in its place instead
 * (the buyer still gets a join/share surface, never an empty gap). The caller
 * passes only the agents to show: `HomeView` leaves out the agent whose
 * made-for-you offer card sits above, so no agent appears on Home twice.
 */
export function AgentPreview({
  agents,
  areaNotLaunched,
  areaLabel,
  launchedAreas,
  sectionState,
  tier,
  profileHref = DEFAULT_PROFILE_HREF,
  onAgentClick,
}: AgentPreviewProps) {
  if (areaNotLaunched) {
    return (
      <section data-testid="home-agent-preview">
        <BrowseWaitlistState
          areaLabel={areaLabel || "your area"}
          embedded
          launchedAreas={launchedAreas}
        />
      </section>
    );
  }

  const top = topAgentsBySavings(agents, 3);
  if (top.length === 0) return null;

  return (
    <section className="space-y-3" data-testid="home-agent-preview">
      <SectionLabel
        label={agentPreviewSectionLabel(sectionState, areaLabel, tier)}
      />
      <div className="@container">
        <div className="grid grid-cols-1 gap-3 @[42rem]:grid-cols-3">
          {top.map((agent, index) => (
            <ProBrowseCard
              agent={agent}
              compact
              key={agent.identity.pro_user_id}
              onClick={() => onAgentClick?.(index + 1)}
              profileHref={profileHref(agent.identity.pro_user_id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
